import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { cn } from '~/lib/cn';
import { ArtistExportDialog } from '~/modules/artist-roster/components/ArtistExport/ArtistExportDialog';
import { getRosterStatus } from '~/modules/artist-roster/lib/roster';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { useIsContactInfoEnabled } from '../../hooks/useIsContactInfoEnabled';
import { exportArtistsCSV } from '../../lib/artists';

interface ArtistExportProps {
  search?: string;
  labels?: number[];
  isCrossover?: boolean;
  isOnRoster?: boolean;
  className?: string;
}

const ArtistExport = ({
  search = '',
  className = '',
  isCrossover = false,
  isOnRoster = undefined,
  labels = [],
}: ArtistExportProps) => {
  const { t } = useTranslation('roster');
  const [isExporting, setIsExporting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { trackEvent } = useAnalytics('Artist Roster');
  const isContactDetailsEnabled = useIsContactInfoEnabled();

  const handleExportButtonClick = async () => {
    if (isContactDetailsEnabled) {
      setIsDialogOpen(true);
      return;
    }

    setIsExporting(true);
    setIsDialogOpen(false);

    const isExportingComplete = await exportArtistsCSV({
      search,
      labels,
      isCrossover,
      isOnRoster,
    });

    if (isExportingComplete) {
      trackEvent('rosterExported', {
        roster_label_filters_count: labels.length,
        roster_label_filters_applied: labels.length > 0,
        roster_crossover_filter_applied: isCrossover,
        roster_status: getRosterStatus(isOnRoster),
      });
    }

    setIsExporting(false);
  };

  const onExportClick = async (withContactInfo: boolean) => {
    setIsExporting(true);
    setIsDialogOpen(false);

    const isExportingComplete = await exportArtistsCSV({
      search,
      labels,
      isCrossover,
      isOnRoster,
      withContactInfo,
    });

    if (isExportingComplete) {
      trackEvent('rosterExported', {
        roster_label_filters_count: labels.length,
        roster_label_filters_applied: labels.length > 0,
        roster_crossover_filter_applied: isCrossover,
        roster_status: getRosterStatus(isOnRoster),
      });
    }

    setIsExporting(false);
  };

  const handleDialogChange = (state: boolean) => {
    setIsDialogOpen(state);
  };

  return (
    <>
      <Button
        variant="text"
        className={cn(
          'flex items-center justify-center gap-sm font-semibold',
          className,
        )}
        onClick={handleExportButtonClick}
        disabled={isExporting}
      >
        {isExporting ? t('export.labels.exporting') : t('export.labels.export')}
        <div className="inline-block">
          <Icon type="file-export" variant="regular" size="sm" />
        </div>
        <ArtistExportDialog
          isOpen={isDialogOpen}
          onExportClick={onExportClick}
          onChange={handleDialogChange}
        />
      </Button>
    </>
  );
};

ArtistExport.displayName = 'ArtistExport';

export { ArtistExport };
